import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import {
  LinkableSharedItemsDocument,
  SortOrder,
  StringFilterCaseSensitivity,
} from "@/graphql/generated/operations";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";

// Curated, not derived from router.tsx: the route table has far more
// dynamic/nested paths than an admin picker should surface. Order mirrors
// the sidebar's pillar order, with Home first and direct links to Profile, Map and Play.
const HINT_LINK_PAGES = [
  { id: "home", path: "/", labelId: "adminSettings.branding.linkPicker.page.home", defaultLabel: "Home" },
  { id: "profile", path: "/save/basics", labelId: "nav.profile", defaultLabel: "Profile" },
  { id: "map", path: "/grow/map", labelId: "nav.map", defaultLabel: "Map" },
  {
    id: "growPlay",
    path: "/grow/play",
    labelId: "adminSettings.branding.linkPicker.page.growPlay",
    defaultLabel: "Grow · Play",
  },
  { id: "share", path: "/share", labelId: "adminSettings.branding.linkPicker.page.share", defaultLabel: "Share" },
  { id: "match", path: "/match", labelId: "adminSettings.branding.linkPicker.page.match", defaultLabel: "Match" },
] as const;

type Step =
  | { kind: "pick" }
  | { kind: "shareSearch" }
  | { kind: "confirm"; path: string; text: string };

export function WelcomeMessageLinkPicker({ onInsert }: { onInsert: (markdown: string) => void }) {
  const intl = useIntl();
  const label = intl.formatMessage({
    id: "adminSettings.branding.linkPicker.trigger",
    defaultMessage: "Link einfügen",
  });
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>({ kind: "pick" });
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const shareItems = useQuery(LinkableSharedItemsDocument, {
    variables: {
      input: {
        first: 20,
        order: { name: SortOrder.Asc },
        ...(trimmed.length >= 2
          ? { where: { name: { contains: trimmed, caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive } } }
          : {}),
      },
    },
    fetchPolicy: "cache-and-network",
    skip: step.kind !== "shareSearch",
  });

  function reset() {
    setOpen(false);
    setStep({ kind: "pick" });
    setQuery("");
  }

  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) reset();
    }
    function onDocKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") reset();
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, [open]);

  function confirm() {
    if (step.kind !== "confirm") return;
    const text = step.text.trim() || step.path;
    onInsert(`[${text}](${step.path})`);
    reset();
  }

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <Button
        variant="secondary"
        size="compact"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={label}
          className="absolute left-0 top-full z-[var(--z-dropdown)] mt-1 w-72 rounded-md bg-surface p-2 shadow-lg ring-1 ring-ink/10"
        >
          {step.kind === "pick" && (
            <div className="space-y-0.5">
              {HINT_LINK_PAGES.map((page) => (
                <MenuItem
                  key={page.id}
                  onClick={() =>
                    setStep({
                      kind: "confirm",
                      path: page.path,
                      text: intl.formatMessage({ id: page.labelId, defaultMessage: page.defaultLabel }),
                    })
                  }
                >
                  {intl.formatMessage({ id: page.labelId, defaultMessage: page.defaultLabel })}
                </MenuItem>
              ))}
              <MenuItem onClick={() => setStep({ kind: "shareSearch" })}>
                {intl.formatMessage({
                  id: "adminSettings.branding.linkPicker.shareItem",
                  defaultMessage: "Bestimmtes Share-Item …",
                })}
              </MenuItem>
            </div>
          )}

          {step.kind === "shareSearch" && (
            <div className="space-y-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "adminSettings.branding.linkPicker.searchPlaceholder",
                  defaultMessage: "Nach Name suchen …",
                })}
                className="bg-page w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30"
              />
              <div className="max-h-56 space-y-0.5 overflow-auto">
                {(shareItems.data?.sharedItems.nodes ?? []).map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={() =>
                      setStep({
                        kind: "confirm",
                        path: `/share/discover/${item.id}`,
                        text:
                          item.name ??
                          intl.formatMessage({
                            id: "adminSettings.branding.linkPicker.unnamedShareItem",
                            defaultMessage: "Unbenanntes Share-Item",
                          }),
                      })
                    }
                  >
                    {item.name ||
                      intl.formatMessage({
                        id: "adminSettings.branding.linkPicker.unnamedShareItem",
                        defaultMessage: "Unbenanntes Share-Item",
                      })}
                  </MenuItem>
                ))}
              </div>
              <Button variant="secondary" size="compact" onClick={() => setStep({ kind: "pick" })}>
                {intl.formatMessage({ id: "common.back", defaultMessage: "Zurück" })}
              </Button>
            </div>
          )}

          {step.kind === "confirm" && (
            <div className="space-y-2 p-1">
              <label className="block">
                <span className="text-[11px] text-ink/55">
                  {intl.formatMessage({
                    id: "adminSettings.branding.linkPicker.displayText",
                    defaultMessage: "Anzeigetext",
                  })}
                </span>
                <input
                  autoFocus
                  value={step.text}
                  onChange={(e) => setStep({ ...step, text: e.target.value })}
                  className="bg-page mt-1 w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30"
                />
              </label>
              <div className="truncate text-[11px] text-ink/45">{step.path}</div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="compact" onClick={() => setStep({ kind: "pick" })}>
                  {intl.formatMessage({ id: "common.back", defaultMessage: "Zurück" })}
                </Button>
                <Button variant="primary" size="compact" onClick={confirm}>
                  {intl.formatMessage({
                    id: "adminSettings.branding.linkPicker.insert",
                    defaultMessage: "Einfügen",
                  })}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
